import { z } from 'zod';
import {
  ORG_TYPES,
  PRODUCT_STATUSES,
  PRODUCT_TYPES,
  RELEASE_STATUSES,
  SLA_LEVELS,
  SUBSCRIPTION_STATUSES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  TICKET_TYPES,
  USER_TYPES,
  WORK_ITEM_STATUSES,
  WORK_ITEM_TYPES,
} from './enums';
import { PERMISSION_KEYS } from './permissions';

const uuid = z.string().uuid();
const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}/, 'Expected a date (YYYY-MM-DD)');
const optionalText = (max: number) => z.string().trim().max(max).optional().nullable();

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(128)
  .refine((p) => /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p), {
    message: 'Password must contain upper-case, lower-case letters and a number',
  });

// ---------- Auth ----------
export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1),
  totp: z.string().trim().regex(/^\d{6}$/).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: z.string().trim().toLowerCase().email() });
export const resetPasswordSchema = z.object({ token: z.string().min(10), password: passwordSchema });
export const acceptInviteSchema = z.object({
  token: z.string().min(10),
  name: z.string().trim().min(1).max(120).optional(),
  password: passwordSchema,
});
export const totpVerifySchema = z.object({ code: z.string().trim().regex(/^\d{6}$/) });
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1), newPassword: passwordSchema });

// ---------- Pagination ----------
export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
  q: z.string().trim().max(200).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

// ---------- Users & roles ----------
export const createUserSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email(),
  userType: z.enum(USER_TYPES),
  organizationId: uuid.optional(),
  roleIds: z.array(uuid).min(1, 'Pick at least one role'),
  productScopeIds: z.array(uuid).default([]),
  sendInvite: z.boolean().default(true),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  roleIds: z.array(uuid).min(1).optional(),
  productScopeIds: z.array(uuid).optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const bulkInviteSchema = z.object({
  /** CSV with header: name,email,role[,organization] */
  csv: z.string().min(1).max(500_000),
  userType: z.enum(USER_TYPES),
  organizationId: uuid.optional(),
});

export const roleSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: optionalText(300),
  scope: z.enum(USER_TYPES),
  permissions: z.array(z.enum(PERMISSION_KEYS as [string, ...string[]])),
});
export type RoleInput = z.infer<typeof roleSchema>;

// ---------- Organizations & subscriptions ----------
export const organizationSchema = z.object({
  name: z.string().trim().min(1).max(160),
  type: z.enum(ORG_TYPES).default('client'),
  industry: optionalText(120),
  address: optionalText(500),
  status: z.enum(['active', 'inactive']).default('active'),
});
export type OrganizationInput = z.infer<typeof organizationSchema>;

export const subscriptionSchema = z.object({
  productId: uuid,
  plan: optionalText(80),
  slaLevel: z.enum(SLA_LEVELS).default('standard'),
  startDate: dateString,
  endDate: dateString.optional().nullable(),
  status: z.enum(SUBSCRIPTION_STATUSES).default('active'),
});
export type SubscriptionInput = z.infer<typeof subscriptionSchema>;

// ---------- Products ----------
export const productSchema = z.object({
  name: z.string().trim().min(1).max(160),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9-]{2,20}$/, 'Code: 2–20 letters, digits or dashes'),
  type: z.enum(PRODUCT_TYPES),
  description: optionalText(5000),
  managerId: uuid.optional().nullable(),
  currentVersion: optionalText(40),
  status: z.enum(PRODUCT_STATUSES).default('active'),
  notifyEmail: z.string().trim().email().optional().nullable().or(z.literal('')),
});
export type ProductInput = z.infer<typeof productSchema>;

export const productModuleSchema = z.object({ name: z.string().trim().min(1).max(120) });
export const releaseSchema = z.object({
  version: z.string().trim().min(1).max(40),
  releaseDate: dateString.optional().nullable(),
  notes: optionalText(10000),
  status: z.enum(RELEASE_STATUSES).default('planned'),
});
export type ReleaseInput = z.infer<typeof releaseSchema>;

// ---------- Tickets ----------
export const createTicketSchema = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().min(1).max(50_000),
  type: z.enum(TICKET_TYPES),
  priority: z.enum(TICKET_PRIORITIES).default('medium'),
  productId: uuid,
  moduleId: uuid.optional().nullable(),
  /** Staff raising a ticket on a client's behalf must give the organization. */
  organizationId: uuid.optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
});
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

export const updateTicketSchema = z.object({
  title: z.string().trim().min(3).max(200).optional(),
  description: z.string().trim().min(1).max(50_000).optional(),
  type: z.enum(TICKET_TYPES).optional(),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  moduleId: uuid.optional().nullable(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type UpdateTicketInput = z.infer<typeof updateTicketSchema>;

const assigneeFilter = z.union([uuid, z.literal('me'), z.literal('none')]);
const reporterFilter = z.union([uuid, z.literal('me')]);
export const ticketListQuerySchema = listQuerySchema.extend({
  status: z.union([z.enum(TICKET_STATUSES), z.array(z.enum(TICKET_STATUSES))]).optional(),
  priority: z.union([z.enum(TICKET_PRIORITIES), z.array(z.enum(TICKET_PRIORITIES))]).optional(),
  type: z.enum(TICKET_TYPES).optional(),
  productId: uuid.optional(),
  organizationId: z.union([uuid, z.array(uuid)]).optional(),
  assigneeId: z.union([assigneeFilter, z.array(assigneeFilter)]).optional(),
  reporterId: z.union([reporterFilter, z.array(reporterFilter)]).optional(),
  overdue: z.coerce.boolean().optional(),
  open: z.coerce.boolean().optional(),
});
export type TicketListQuery = z.infer<typeof ticketListQuerySchema>;

export const assignTicketSchema = z.object({
  assigneeId: uuid,
  dueDate: dateString,
  targetReleaseId: uuid.optional().nullable(),
  internalNote: z.string().trim().max(20_000).optional(),
});
export type AssignTicketInput = z.infer<typeof assignTicketSchema>;

export const changeStatusSchema = z.object({
  status: z.enum(TICKET_STATUSES),
  reason: z.string().trim().max(5000).optional(),
  duplicateOfTicketNo: z.string().trim().max(30).optional(),
});
export type ChangeStatusInput = z.infer<typeof changeStatusSchema>;

export const commentSchema = z.object({
  body: z.string().trim().min(1).max(50_000),
  isInternal: z.boolean().default(false),
});
export type CommentInput = z.infer<typeof commentSchema>;

export const watcherSchema = z.object({ userId: uuid.optional(), remove: z.boolean().default(false) });

// ---------- Work items ----------
export const workItemSchema = z.object({
  productId: uuid,
  ticketId: uuid.optional().nullable(),
  title: z.string().trim().min(3).max(200),
  description: optionalText(20_000),
  type: z.enum(WORK_ITEM_TYPES),
  status: z.enum(WORK_ITEM_STATUSES).default('backlog'),
  assigneeId: uuid.optional().nullable(),
  estimateHours: z.coerce.number().min(0).max(10_000).optional().nullable(),
  startDate: dateString.optional().nullable(),
  targetDate: dateString.optional().nullable(),
  progress: z.coerce.number().int().min(0).max(100).default(0),
  releaseId: uuid.optional().nullable(),
});
export type WorkItemInput = z.infer<typeof workItemSchema>;
export const updateWorkItemSchema = workItemSchema.partial().omit({ productId: true });

// ---------- Notifications ----------
export const NOTIFICATION_EVENTS = [
  'ticket_created',
  'ticket_assigned',
  'public_comment',
  'internal_note',
  'status_changed',
  'due_soon',
  'overdue',
  'sla_breach',
] as const;
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

export const notificationPreferencesSchema = z.record(
  z.enum(NOTIFICATION_EVENTS),
  z.object({ email: z.boolean(), inApp: z.boolean() }),
);
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

// ---------- SLA ----------
export const slaPolicySchema = z.object({
  slaLevel: z.enum(SLA_LEVELS),
  priority: z.enum(TICKET_PRIORITIES),
  firstResponseHours: z.coerce.number().int().min(1).max(10_000),
  resolutionHours: z.coerce.number().int().min(1).max(10_000),
});
export type SlaPolicyInput = z.infer<typeof slaPolicySchema>;
